import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api, { formatMMK } from '@/lib/api'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line,
} from 'recharts'
import { TrendingUp, Users, Handshake, Activity, Clock, ArrowUpRight, ArrowDownRight, Download } from 'lucide-react'

const STAGES = ['Qualification', 'Need Analysis', 'Proposal', 'Negotiation']
const ACTIVITY_TYPES = ['Scheduled Call', 'Scheduled Meeting', 'Task', 'Completed Call', 'Completed Meeting']
const STAGE_COLORS: Record<string, string> = {
  'Qualification': '#7F1D1D', 'Need Analysis': '#B91C1C',
  'Proposal': '#DC2626', 'Negotiation': '#F59E0B',
  'Won': '#10B981', 'Lost': '#6B7280',
}

// ─── Date helpers ──────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10)
}
function firstOfMonth(offset = 0) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset, 1)
  return d.toISOString().slice(0, 10)
}
function lastOfMonth(offset = 0) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset + 1, 0)
  return d.toISOString().slice(0, 10)
}
function firstOfQuarter() {
  const d = new Date()
  const qStart = Math.floor(d.getMonth() / 3) * 3
  d.setMonth(qStart, 1)
  return d.toISOString().slice(0, 10)
}

// ─── Export helpers ────────────────────────────────────────────────────────
function buildExportUrl(path: string, params: Record<string, string>) {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null))).toString()
  return `/api/export${path}${qs ? `?${qs}` : ''}`
}

function ExportButton({ label, href, icon = 'xlsx' }: { label: string; href: string; icon?: 'xlsx' | 'pdf' }) {
  const [loading, setLoading] = useState(false)
  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await fetch(href, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const cd = res.headers.get('Content-Disposition') || ''
      const match = cd.match(/filename="([^"]+)"/)
      a.download = match ? match[1] : label
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // silently ignore – network errors visible in console
    } finally {
      setLoading(false)
    }
  }
  return (
    <a href={href} onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
        ${icon === 'pdf'
          ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100'
          : 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100'}
        ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <Download size={12} />
      {loading ? 'Exporting…' : label}
    </a>
  )
}

// ─── Reusable sub-components ───────────────────────────────────────────────
function Card({ title, children, className = '' }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-4 ${className}`}>
      {title && <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>}
      {children}
    </div>
  )
}

function StatBadge({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className={`rounded-lg p-3 ${color}`}>
      <div className="text-xs font-medium text-white/70">{label}</div>
      <div className="text-2xl font-bold text-white mt-0.5">{value}</div>
      {sub && <div className="text-xs text-white/70 mt-0.5">{sub}</div>}
    </div>
  )
}

function EmptyState({ message = 'No data for the selected filters' }: { message?: string }) {
  return (
    <div className="flex items-center justify-center h-32 text-sm text-slate-400">{message}</div>
  )
}

function Spinner() {
  return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800" />
    </div>
  )
}

function DbErrorState() {
  return (
    <div className="flex flex-col items-center justify-center h-32 gap-2 text-center">
      <div className="text-red-600 text-sm font-semibold">Something went wrong</div>
      <div className="text-xs text-slate-500">A database error occurred — please refresh or contact support.</div>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-sm">
        {label && <p className="text-xs text-slate-500 mb-1 font-medium">{label}</p>}
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.fill || p.color || '#1e293b' }}>
            {p.name}: {typeof p.value === 'number' && p.value > 10000 ? formatMMK(p.value) : p.value}
          </p>
        ))}
      </div>
    )
  }
  return null
}

// ─── Report sections ───────────────────────────────────────────────────────
function ConversionRate({ dateFrom, dateTo, owner }: { dateFrom: string; dateTo: string; owner: string }) {
  const { data, isLoading, isError } = useQuery<any>({
    queryKey: ['crm-conversion', dateFrom, dateTo, owner],
    queryFn: () => api.get('/analytics/crm/conversion-rate', { params: { date_from: dateFrom, date_to: dateTo, owner } }).then(r => r.data),
    staleTime: 30000,
  })
  const exportHref = buildExportUrl('/crm/conversion-rate', { date_from: dateFrom, date_to: dateTo, owner })
  if (isLoading) return <Spinner />
  if (isError) return <DbErrorState />
  if (!data) return <EmptyState />
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportButton label="Export PDF" href={exportHref} icon="pdf" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBadge label="Total Leads Created" value={data.total_leads} color="bg-red-800" />
        <StatBadge label="Converted to Contact" value={data.total_contacts} color="bg-purple-700" />
        <StatBadge label="Conversion Rate" value={`${data.conversion_rate}%`} color="bg-amber-600" />
        <StatBadge label="Total Records" value={data.total} color="bg-slate-600" />
      </div>
      {data.trend?.length > 1 && (
        <Card title="Monthly Trend — Leads vs Converted Contacts">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="leads" name="Leads" stroke="#7F1D1D" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="contacts" name="Converted" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  )
}

function WinLossReport({ dateFrom, dateTo, owner }: { dateFrom: string; dateTo: string; owner: string }) {
  const { data, isLoading, isError } = useQuery<any>({
    queryKey: ['crm-win-loss', dateFrom, dateTo, owner],
    queryFn: () => api.get('/analytics/crm/win-loss-by-stage', { params: { date_from: dateFrom, date_to: dateTo, owner } }).then(r => r.data),
    staleTime: 30000,
  })
  const exportHref = buildExportUrl('/crm/win-loss', { date_from: dateFrom, date_to: dateTo, owner })
  if (isLoading) return <Spinner />
  if (isError) return <DbErrorState />
  if (!data) return <EmptyState />
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportButton label="Export PDF" href={exportHref} icon="pdf" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBadge label="Won Deals" value={data.total_won} sub={formatMMK(data.won_value)} color="bg-green-700" />
        <StatBadge label="Lost Deals" value={data.total_lost} color="bg-slate-600" />
        <StatBadge label="Win Rate" value={`${data.win_rate}%`} color="bg-amber-600" />
        <StatBadge label="Total Closed" value={data.total_won + data.total_lost} color="bg-purple-700" />
      </div>
      {data.by_stage?.length > 0 && (
        <Card title="Deals by Stage — Won vs Lost">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_stage} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="stage" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="won" name="Won" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="lost" name="Lost" fill="#EF4444" radius={[3, 3, 0, 0]} />
              <Bar dataKey="open" name="Open" fill="#94A3B8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  )
}

function ActivityLogReport({ dateFrom, dateTo, owner }: { dateFrom: string; dateTo: string; owner: string }) {
  const [actType, setActType] = useState('')
  const [page, setPage] = useState(1)
  const perPage = 20

  const { data, isLoading, isError } = useQuery<any>({
    queryKey: ['crm-activity-log', dateFrom, dateTo, owner, actType, page],
    queryFn: () => api.get('/analytics/crm/activity-log', {
      params: { date_from: dateFrom, date_to: dateTo, owner, activity_type: actType, page, per_page: perPage }
    }).then(r => r.data),
    staleTime: 30000,
  })

  const exportHref = buildExportUrl('/crm/activity-log', { date_from: dateFrom, date_to: dateTo, owner, activity_type: actType })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={actType} onChange={e => { setActType(e.target.value); setPage(1) }}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
          <option value="">All Activity Types</option>
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="ml-auto">
          <ExportButton label="Export Excel" href={exportHref} icon="xlsx" />
        </div>
      </div>
      {isLoading ? <Spinner /> : isError ? <DbErrorState /> : !data?.data?.length ? <EmptyState /> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Date', 'Type', 'Subject', 'Linked To', 'Owner', 'Logged By'].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.data.map((a: any) => (
                  <tr key={`${a.entity_type}-${a.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{a.activity_date || '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.activity_type?.includes('Call') ? 'bg-red-100 text-red-800' : a.activity_type?.includes('Meeting') ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}`}>
                        {a.activity_type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 max-w-[180px] truncate">{a.subject}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded mr-1 ${a.entity_type === 'deal' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                        {a.entity_type === 'deal' ? 'Deal' : 'Contact'}
                      </span>
                      <span className="text-slate-700">{a.entity_name || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{a.owner || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{a.created_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-500">
              <span>{data.total} activities</span>
              <div className="flex gap-1">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                  className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">‹</button>
                <span className="px-2 py-1">Page {page} of {data.total_pages}</span>
                <button disabled={page === data.total_pages} onClick={() => setPage(p => p + 1)}
                  className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">›</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function OwnerPerformance({ dateFrom, dateTo, owner }: { dateFrom: string; dateTo: string; owner: string }) {
  const { data, isLoading, isError } = useQuery<any[]>({
    queryKey: ['crm-owner-perf', dateFrom, dateTo, owner],
    queryFn: () => api.get('/analytics/crm/owner-performance', { params: { date_from: dateFrom, date_to: dateTo, owner } }).then(r => r.data),
    staleTime: 30000,
  })
  const exportHref = buildExportUrl('/crm/owner-performance', { date_from: dateFrom, date_to: dateTo, owner })
  if (isLoading) return <Spinner />
  if (isError) return <DbErrorState />
  if (!data?.length) return <EmptyState />
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <ExportButton label="Export Excel" href={exportHref} icon="xlsx" />
      </div>
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {['Owner', 'Open Deals', 'Pipeline Value', 'Won (Period)', 'Won Value', 'Lost (Period)', 'Win Rate'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.map((row: any) => (
              <tr key={row.owner} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 font-medium text-slate-800">{row.owner}</td>
                <td className="px-3 py-2.5 text-slate-700">{row.open_deals}</td>
                <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{formatMMK(row.pipeline_value)}</td>
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-1 text-green-700 font-medium">
                    <ArrowUpRight size={14} />{row.won_deals}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-green-700 whitespace-nowrap">{formatMMK(row.won_value)}</td>
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-1 text-red-600 font-medium">
                    <ArrowDownRight size={14} />{row.lost_deals}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5 max-w-[60px]">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${Math.min(row.win_rate, 100)}%` }} />
                    </div>
                    <span className="text-slate-700 tabular-nums">{row.win_rate}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  )
}

function AgingDeals({ owner }: { owner: string }) {
  const [stageFilter, setStageFilter] = useState('')
  const { data, isLoading, isError } = useQuery<any[]>({
    queryKey: ['crm-aging', owner, stageFilter],
    queryFn: () => api.get('/analytics/crm/aging-deals', { params: { owner, stage: stageFilter } }).then(r => r.data),
    staleTime: 30000,
  })
  const exportHref = buildExportUrl('/crm/aging-deals', { owner, stage: stageFilter })
  if (isLoading) return <Spinner />
  if (isError) return <DbErrorState />
  if (!data?.length) return <EmptyState message="No open deals found" />

  const rowColor = (days: number) =>
    days > 30 ? 'bg-red-50 border-l-4 border-l-red-500' :
    days > 14 ? 'bg-amber-50 border-l-4 border-l-amber-400' :
    'bg-white border-l-4 border-l-green-400'

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
          <option value="">All Stages</option>
          {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />＜14 days</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />14–30 days</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />＞30 days</span>
        </div>
        <div className="ml-auto">
          <ExportButton label="Export Excel" href={exportHref} icon="xlsx" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Deal', 'Contact', 'Stage', 'Owner', 'Value', 'Days in Stage'].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((d: any) => (
                <tr key={d.id} className={`${rowColor(d.days_in_stage)}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{d.deal_name}</td>
                  <td className="px-3 py-2.5 text-slate-600">{d.client_name || '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: STAGE_COLORS[d.stage] || '#94A3B8' }}>
                      {d.stage}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{d.owner}</td>
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{formatMMK(d.value)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`font-semibold tabular-nums ${d.days_in_stage > 30 ? 'text-red-600' : d.days_in_stage > 14 ? 'text-amber-600' : 'text-green-600'}`}>
                      {d.days_in_stage}d
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'conversion', label: 'Conversion Rate', icon: Users },
  { id: 'winloss', label: 'Win / Loss', icon: Handshake },
  { id: 'activity', label: 'Activity Log', icon: Activity },
  { id: 'performance', label: 'Owner Performance', icon: TrendingUp },
  { id: 'aging', label: 'Aging Deals', icon: Clock },
]

const PRESETS = [
  { label: 'This Month', from: () => firstOfMonth(), to: () => todayStr() },
  { label: 'Last Month', from: () => firstOfMonth(-1), to: () => lastOfMonth(-1) },
  { label: 'This Quarter', from: () => firstOfQuarter(), to: () => todayStr() },
]

export default function CrmReports() {
  const [activeTab, setActiveTab] = useState('conversion')
  const [dateFrom, setDateFrom] = useState(firstOfMonth())
  const [dateTo, setDateTo] = useState(todayStr())
  const [ownerFilter, setOwnerFilter] = useState('')
  const [activePreset, setActivePreset] = useState('This Month')

  const { data: crmOwners } = useQuery<{ id: number; full_name: string }[]>({
    queryKey: ['crm-owners'],
    queryFn: () => api.get('/users/crm-owners').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const applyPreset = (preset: typeof PRESETS[0]) => {
    setDateFrom(preset.from())
    setDateTo(preset.to())
    setActivePreset(preset.label)
  }

  const handleCustomDate = (field: 'from' | 'to', val: string) => {
    if (field === 'from') setDateFrom(val)
    else setDateTo(val)
    setActivePreset('Custom')
  }

  const ActiveIcon = TABS.find(t => t.id === activeTab)?.icon || TrendingUp

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <TrendingUp size={22} className="text-red-700" />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">CRM Reports</h1>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap gap-2 items-center">
        {/* Presets */}
        {PRESETS.map(p => (
          <button key={p.label} onClick={() => applyPreset(p)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${activePreset === p.label ? 'bg-red-800 text-white border-red-800' : 'border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-700'}`}>
            {p.label}
          </button>
        ))}
        {/* Custom dates */}
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <input type="date" value={dateFrom} onChange={e => handleCustomDate('from', e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          <span>–</span>
          <input type="date" value={dateTo} onChange={e => handleCustomDate('to', e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
        </div>
        {/* Owner filter */}
        {crmOwners && crmOwners.length > 1 && (
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 ml-auto">
            <option value="">All Owners</option>
            {crmOwners.map(o => <option key={o.id} value={o.full_name}>{o.full_name}</option>)}
          </select>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <Icon size={15} />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Report content */}
      <div>
        {activeTab === 'conversion' && <ConversionRate dateFrom={dateFrom} dateTo={dateTo} owner={ownerFilter} />}
        {activeTab === 'winloss' && <WinLossReport dateFrom={dateFrom} dateTo={dateTo} owner={ownerFilter} />}
        {activeTab === 'activity' && <ActivityLogReport dateFrom={dateFrom} dateTo={dateTo} owner={ownerFilter} />}
        {activeTab === 'performance' && <OwnerPerformance dateFrom={dateFrom} dateTo={dateTo} owner={ownerFilter} />}
        {activeTab === 'aging' && <AgingDeals owner={ownerFilter} />}
      </div>
    </div>
  )
}
